'use client';

import { c as _c } from "react/compiler-runtime";
import { type CreateDuplicateInput, CreateDuplicateInputSchema, getApiErrorMessage, useApi } from '@repo/api';
import { useMutation } from '@repo/tanstack/query';
import { isResponseError, type ResponseError } from '@repo/upfetch';
import { useRef, useState } from 'react';
import { type DuplicateSaveOutcome, IDLE, saveInCardOrder, SKIPPED } from '@/app/@protected/loads/_utils/duplicate-save.ts';
const FAILURE_MESSAGE = 'Unable to create the Duplicate.';
const toFailure = (error: ResponseError): DuplicateSaveOutcome => ({
  errors: getApiErrorMessage(error, FAILURE_MESSAGE),
  status: 'failed'
});

/**
 * Saves Duplicates of the Original Load `loadId`, one card at a time, each sent once and never
 * retried: the API creates a Load for every request. `outcomes` holds each card's save, by index,
 * for one card, starting at `initial` or idle, until `resize` changes how many there are; `reset`
 * starts a new card. `onEachCreated` receives each new Load as soon as the API creates it.
 */
export const useDuplicateSave = (loadId, initial, onEachCreated) => {
  const $ = _c(23);
  const api = useApi();
  const t0 = initial ?? IDLE;
  let t1;
  if ($[0] !== t0) {
    t1 = [t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [outcomes, setOutcomes] = useState(t1);
  const savingRef = useRef(false);
  let t2;
  if ($[2] !== api.loads.duplicate.create || $[3] !== api.loads.list || $[4] !== loadId) {
    t2 = api.loads.duplicate.create.mutationOptions({
      path: {
        loadId
      }
    }, {
      meta: {
        invalidates: [api.loads.list.allScopesKey({
          input: {}
        })]
      }
    });
    $[2] = api.loads.duplicate.create;
    $[3] = api.loads.list;
    $[4] = loadId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    mutateAsync
  } = useMutation(t2);
  let t3;
  if ($[6] !== mutateAsync || $[7] !== onEachCreated) {
    t3 = async input => {
      if (!CreateDuplicateInputSchema.safeParse(input).success) {
        return {
          errors: [{
            message: FAILURE_MESSAGE
          }],
          status: "failed"
        };
      }
      ;
      try {
        const load = await mutateAsync(input);
        onEachCreated(load.id);
        return {
          loadId: load.id,
          status: "created"
        };
      } catch (t4) {
        const error = t4;
        return isResponseError(error) ? toFailure(error) : {
          status: "unconfirmed"
        };
      }
    };
    $[6] = mutateAsync;
    $[7] = onEachCreated;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const send = t3;
  let t4;
  if ($[9] !== outcomes || $[10] !== send) {
    t4 = async inputs => {
      if (savingRef.current) {
        return null;
      }
      savingRef.current = true;
      const next = await saveInCardOrder(inputs, outcomes, send, setOutcomes);
      savingRef.current = false;
      return next;
    };
    $[9] = outcomes;
    $[10] = send;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const save = t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = count => setOutcomes(current => Array.from({
      length: count
    }, (_, index) => current[index] ?? IDLE));
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const resize = t5;
  let t6;
  if ($[13] !== outcomes) {
    t6 = index_0 => {
      const next_0 = outcomes.with(index_0, SKIPPED);
      setOutcomes(next_0);
      return next_0;
    };
    $[13] = outcomes;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const skip = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setOutcomes([IDLE]);
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== outcomes) {
    t8 = outcomes.some(_temp);
    $[16] = outcomes;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== outcomes || $[19] !== save || $[20] !== skip || $[21] !== t8) {
    t9 = {
      outcomes,
      reset: t7,
      resize,
      save,
      sending: t8,
      skip
    };
    $[18] = outcomes;
    $[19] = save;
    $[20] = skip;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
function _temp(outcome) {
  return outcome.status === "sending";
}